import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import isTailwindBreakpoint from '../utils/isTailwindBreakpoint';

gsap.registerPlugin(ScrollTrigger);

export default class ProductsShowcase extends HTMLElement {
  private heading: HTMLHeadingElement | null = null;
  private navButtons: HTMLButtonElement[] = [];
  private infos: HTMLDivElement[] = [];
  private sliderInfos: HTMLDivElement[] = [];
  private descriptions: HTMLDivElement[] = [];
  private imagesContainer: HTMLDivElement | null = null;
  private images: HTMLDivElement[] = [];
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;

  private scrollTl: gsap.core.Timeline | null = null;
  private goToItemTl: gsap.core.Timeline | null = null;
  private onResizeBound = this.onResize.bind(this);
  private onNavButtonClickBound = this.onNavButtonClick.bind(this);
  private onPrevButtonClickBound = this.onPrevButtonClick.bind(this);
  private onNextButtonClickBound = this.onNextButtonClick.bind(this);

  private previousItemIndex: number | null = null;
  private currentItemIndex: number = 0;

  connectedCallback(): void {
    this.heading = this.querySelector('[data-products-showcase-ref="heading"]');
    this.navButtons = Array.from(
      this.querySelectorAll('[data-products-showcase-ref="nav-buttons"]'),
    ) as HTMLButtonElement[];
    this.infos = Array.from(this.querySelectorAll('[data-products-showcase-ref="infos"]')) as HTMLDivElement[];
    this.sliderInfos = Array.from(
      this.querySelectorAll('[data-products-showcase-ref="slider-infos"]'),
    ) as HTMLDivElement[];
    this.descriptions = Array.from(
      this.querySelectorAll('[data-products-showcase-ref="descriptions"]'),
    ) as HTMLDivElement[];
    this.imagesContainer = this.querySelector('[data-products-showcase-ref="images-container"]') as HTMLDivElement;
    this.images = Array.from(this.querySelectorAll('[data-products-showcase-ref="images"]')) as HTMLDivElement[];
    this.prevButton = this.querySelector('[data-products-showcase-ref="prev-button"]') as HTMLButtonElement;
    this.nextButton = this.querySelector('[data-products-showcase-ref="next-button"]') as HTMLButtonElement;

    this.createScrollAnimation();

    this.addEventListeners();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);
    this.onResize();

    if (this.navButtons) {
      this.navButtons.forEach((button) => {
        button.addEventListener('click', this.onNavButtonClickBound);
      });
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onPrevButtonClickBound);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNextButtonClickBound);
    }
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);

    if (this.navButtons) {
      this.navButtons.forEach((button) => {
        button.removeEventListener('click', this.onNavButtonClickBound);
      });
    }

    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.onPrevButtonClickBound);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.onNextButtonClickBound);
    }
  }

  onNavButtonClick(event: Event): void {
    if (!this.navButtons) return;

    const button = event.currentTarget as HTMLButtonElement;
    const index = Array.from(this.navButtons).indexOf(button);

    if (index === -1) return;

    this.goToItem(index);
  }

  onPrevButtonClick(event: Event): void {
    if (!this.prevButton) return;

    const index = this.currentItemIndex - 1 < 0 ? this.infos.length - 1 : this.currentItemIndex - 1;

    this.goToItem(index);
  }

  onNextButtonClick(event: Event): void {
    if (!this.nextButton) return;

    const index = (this.currentItemIndex + 1) % this.infos.length;
    this.goToItem(index);
  }

  goToItem(index: number): void {
    if (index === this.currentItemIndex) return;

    this.previousItemIndex = this.currentItemIndex;
    this.currentItemIndex = index;

    if (this.goToItemTl) {
      this.goToItemTl.kill();
      this.goToItemTl = null;
    }

    this.goToItemTl = gsap.timeline();

    if (this.previousItemIndex !== null) {
      const previousButton = this.navButtons[this.previousItemIndex];
      const previousInfo = this.infos[this.previousItemIndex];
      const previousSliderInfo = this.sliderInfos[this.previousItemIndex];
      const previousDescription = this.descriptions[this.previousItemIndex];
      const previousImage = this.images[this.previousItemIndex];

      if (previousButton) {
        previousButton.classList.remove('border-charcoal');
        previousButton.classList.add('border-transparent');
      }

      this.goToItemTl.to(previousInfo, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
      this.goToItemTl.to(previousSliderInfo, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
      this.goToItemTl.to(previousDescription, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
      this.goToItemTl.to(previousImage, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);

      this.goToItemTl.set(
        [previousInfo, previousSliderInfo, previousDescription, previousImage],
        { position: 'absolute' },
        0.3,
      );
    }
    const currentButton = this.navButtons[index];
    const currentInfo = this.infos[index];
    const currentSliderInfo = this.sliderInfos[index];
    const currentDescription = this.descriptions[index];
    const currentImage = this.images[index];

    if (currentButton) {
      currentButton.classList.remove('border-transparent');
      currentButton.classList.add('border-charcoal');
    }

    this.goToItemTl.set(
      [currentInfo, currentSliderInfo, currentDescription, currentImage],
      { position: 'relative' },
      0.3,
    );

    this.goToItemTl.to(currentInfo, { autoAlpha: 1, duration: 0.4, ease: 'power2.out' }, 0.4);
    this.goToItemTl.to(currentSliderInfo, { autoAlpha: 1, duration: 0.4, ease: 'power2.out' }, 0.4);
    this.goToItemTl.to(currentDescription, { autoAlpha: 1, duration: 0.4, ease: 'power2.out' }, 0.4);
    this.goToItemTl.to(currentImage, { autoAlpha: 1, duration: 0.4, ease: 'power2.out' }, 0.4);
  }

  createScrollAnimation(): void {
    if (!isTailwindBreakpoint('lg') || !this.heading) {
      return;
    }

    this.scrollTl = gsap.timeline({
      scrollTrigger: {
        trigger: this,
        start: 'top top',
        end: '20% top',
        scrub: true,
      },
    });
    this.scrollTl.fromTo(
      this.heading,
      {
        autoAlpha: 1,
      },
      {
        autoAlpha: 0,
        duration: 1,
        ease: 'linear',
      },
      0,
    );
  }

  killScrollAnimation(): void {
    if (this.scrollTl) {
      this.scrollTl.kill();
      this.scrollTl = null;
    }

    if (this.heading) {
      gsap.set(this.heading, { autoAlpha: 1 });
    }
  }

  onResize(): void {
    const isDesktop = isTailwindBreakpoint('lg');

    if (isDesktop && !this.scrollTl) {
      this.createScrollAnimation();
    } else if (!isDesktop && this.scrollTl) {
      this.killScrollAnimation();
    }

    let tallestImageHeight = 0;
    if (this.imagesContainer) {
      this.images.forEach((image) => {
        const imageHeight = image.getBoundingClientRect().height;
        if (imageHeight > tallestImageHeight) {
          tallestImageHeight = imageHeight;
        }
      });
    }

    if (this.imagesContainer) {
      gsap.set(this.imagesContainer, { height: tallestImageHeight });
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.killScrollAnimation();
  }
}

customElements.define('products-showcase', ProductsShowcase);
